import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Collapsible,
  Stat,
  SimpleGrid
} from "@chakra-ui/react"
import { useState, useRef } from "react"
import { parseInfluxCSV, readCSVFile, ParsedDataPoint } from "@hydrologiq/shared/utils/csvParser"

interface ForecastComparisonPanelProps {
  comparisonData: ParsedDataPoint[] | null
  onComparisonDataChange: (data: ParsedDataPoint[] | null) => void
  accuracyMetrics?: {
    mae: number
    rmse: number
    mape: number
    overlappingPoints: number
  } | null
}

export default function ForecastComparisonPanel({
  comparisonData,
  onComparisonDataChange,
  accuracyMetrics
}: ForecastComparisonPanelProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setError(null)

    try {
      const csvContent = await readCSVFile(file)
      const parsedData = parseInfluxCSV(csvContent)

      if (parsedData.length === 0) {
        throw new Error('No weight data found in CSV file')
      }

      onComparisonDataChange(parsedData)
      setIsOpen(true) // Expand panel when data is loaded
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse CSV file'
      setError(errorMessage)
      console.error('CSV parsing error:', err)
    } finally {
      setIsUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleClear = () => {
    onComparisonDataChange(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <Box borderWidth="1px" borderColor="border" borderRadius="md" overflow="hidden">
      <Collapsible.Root open={isOpen} onOpenChange={(details) => setIsOpen(details.open)}>
        <Collapsible.Trigger asChild>
          <Button
            variant="ghost"
            width="100%"
            justifyContent="space-between"
            p={4}
            borderRadius={0}
            _hover={{ bg: "bg.muted" }}
          >
            <HStack>
              <Text fontWeight="medium">Debug/Comparison</Text>
              {comparisonData && (
                <Text fontSize="sm" color="fg.muted">
                  ({comparisonData.length} data points)
                </Text>
              )}
            </HStack>
            <Text fontSize="sm" color="fg.muted">
              {isOpen ? "▲" : "▼"}
            </Text>
          </Button>
        </Collapsible.Trigger>

        <Collapsible.Content>
          <Box p={4} borderTopWidth="1px" borderColor="border">
            <VStack align="stretch" gap={4}>
              {/* File Upload Section */}
              <HStack gap={2}>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  size="sm"
                />
                <Button
                  onClick={handleClear}
                  variant="outline"
                  size="sm"
                  disabled={!comparisonData}
                >
                  Clear
                </Button>
              </HStack>

              {/* Error Display */}
              {error && (
                <Box p={3} bg="red.50" borderColor="red.200" borderWidth="1px" borderRadius="md">
                  <Text fontSize="sm" color="red.600">
                    {error}
                  </Text>
                </Box>
              )}

              {/* Loading State */}
              {isUploading && (
                <Text fontSize="sm" color="fg.muted">
                  Parsing CSV file...
                </Text>
              )}

              {/* Data Summary */}
              {comparisonData && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Comparison Data Loaded
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    {comparisonData.length} data points from{' '}
                    {new Date(comparisonData[0].date).toLocaleDateString()} to{' '}
                    {new Date(comparisonData[comparisonData.length - 1].date).toLocaleDateString()}
                  </Text>
                  <Text fontSize="xs" color="fg.muted" mt={1}>
                    Data will be filtered to forecast period and aggregated to match intervals.
                    Weight increases {'>'}5kg are filtered out.
                  </Text>
                </Box>
              )}

              {/* Accuracy Metrics */}
              {accuracyMetrics && accuracyMetrics.overlappingPoints > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={3}>
                    Accuracy (overlapping period):
                  </Text>
                  <SimpleGrid columns={3} gap={4}>
                    <Stat.Root>
                      <Stat.Label fontSize="xs">MAE</Stat.Label>
                      <Stat.ValueText fontSize="sm">
                        {accuracyMetrics.mae.toFixed(2)} kg
                      </Stat.ValueText>
                    </Stat.Root>
                    <Stat.Root>
                      <Stat.Label fontSize="xs">RMSE</Stat.Label>
                      <Stat.ValueText fontSize="sm">
                        {accuracyMetrics.rmse.toFixed(2)} kg
                      </Stat.ValueText>
                    </Stat.Root>
                    <Stat.Root>
                      <Stat.Label fontSize="xs">MAPE</Stat.Label>
                      <Stat.ValueText fontSize="sm">
                        {accuracyMetrics.mape.toFixed(1)}%
                      </Stat.ValueText>
                    </Stat.Root>
                  </SimpleGrid>
                  <Text fontSize="xs" color="fg.muted" mt={2}>
                    Based on {accuracyMetrics.overlappingPoints} overlapping data points
                  </Text>
                </Box>
              )}

              {/* Help Text */}
              {!comparisonData && (
                <Box p={3} bg="bg.muted" borderRadius="md">
                  <Text fontSize="xs" color="fg.muted">
                    Upload a CSV file with InfluxDB format containing weight readings to compare against the forecast.
                    Expected columns: _time, _value, _field (where _field = "weight")
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  )
}